import { Component, OnDestroy } from '@angular/core'
import { Subscription } from 'rxjs'
import {
  IAssistantPendingAction,
  isPendingActionConfirmable,
  isPendingActionWithdrawn,
  withdrawnReasonKey,
} from '../../models/assistant-pending-action.model'
import { AssistantPendingActionService } from '../../services/assistant-pending-action.service'

/**
 * Confirmation card (design-contract.md → §Surface 4), docked inline in the story-4.2 stream after the
 * run-step rows. Self-manages visibility from the live `pendingAction$` doc — renders nothing unless the
 * action is confirmable OR withdrawn (so the withdrawn explanation survives the correlated step flipping
 * off `awaiting_confirmation`). Three distinct states: awaiting (frozen args + Confirm), submit-failed
 * (keeps the frozen args, re-enables Confirm, inline error), and withdrawn (muted explanation, no Confirm).
 */
@Component({
  selector: 'assistant-confirmation-card',
  template: `
    <ng-container *ngrxLet="pendingAction$ as action">
      <ng-container *ngIf="action && (isConfirmable(action) || isWithdrawn(action))">
        <e11-card [isClickable]="false" containerClass="e11-rounded-md e11-px-3 e11-py-2">
          <!-- Confirmable: frozen args (read-only) + Confirm -->
          <ng-container *ngIf="isConfirmable(action); else withdrawn">
            <div class="e11-flex e11-items-center e11-gap-2 e11-mb-2">
              <e11-tag [color]="'warning'" [value]="'Awaiting confirmation' | translate" [inline]="true"></e11-tag>
              <span class="e11-text-sm e11-font-primary-demibold e11-text-skin-base">{{ 'Confirm action' | translate }}</span>
            </div>

            <div class="e11-flex e11-flex-col e11-gap-1 e11-text-sm e11-text-skin-base">
              <div *ngIf="action.args?.job" class="e11-flex e11-gap-2">
                <span class="e11-font-primary-medium e11-text-skin-light">{{ 'Opening' | translate }}:</span>
                <span>{{ action.args?.job }}</span>
              </div>
              <div *ngIf="action.args?.person" class="e11-flex e11-gap-2">
                <span class="e11-font-primary-medium e11-text-skin-light">{{ 'Person' | translate }}:</span>
                <span>{{ action.args?.person }}</span>
              </div>
              <div *ngIf="action.args?.content" class="e11-flex e11-gap-2">
                <span class="e11-font-primary-medium e11-text-skin-light">{{ 'Content' | translate }}:</span>
                <span>{{ action.args?.content }}</span>
              </div>
            </div>

            <p *ngIf="submitFailed" class="e11-text-xs e11-text-skin-error e11-mt-2 e11-mb-0">
              {{ 'Could not submit — try again.' | translate }}
            </p>

            <div class="e11-mt-3">
              <e11-button
                [color]="'primary'"
                [value]="'Confirm' | translate"
                [loading]="submitting"
                [loadingText]="'Confirm' | translate"
                [disabled]="submitting"
                (click)="confirm(action)"
              ></e11-button>
            </div>
          </ng-container>

          <!-- Withdrawn (expired / superseded): muted explanation, neutral pill, no Confirm -->
          <ng-template #withdrawn>
            <div class="e11-flex e11-items-center e11-gap-2 e11-mb-2">
              <e11-tag [color]="'neutral'" [value]="'Withdrawn' | translate" [inline]="true"></e11-tag>
            </div>
            <p class="e11-text-sm e11-text-gray-500 e11-mb-0">{{ withdrawnReasonKey(action.status) | translate }}</p>
          </ng-template>
        </e11-card>
      </ng-container>
    </ng-container>
  `,
})
export class AssistantConfirmationCardComponent implements OnDestroy {
  pendingAction$ = this.pendingActionService.pendingAction$

  /** True while the confirm POST is in flight — drives the button loader and disables Confirm. */
  submitting = false
  /** True after a non-409 confirm failure — keeps the frozen args, re-enables Confirm, shows inline error. */
  submitFailed = false

  /** The id of the action the submit flags belong to — flags reset when the live doc becomes a new action. */
  private submitStateActionId?: string
  private sub = new Subscription()

  isConfirmable = (action: IAssistantPendingAction) => isPendingActionConfirmable(action.status)
  isWithdrawn = (action: IAssistantPendingAction) => isPendingActionWithdrawn(action.status)
  withdrawnReasonKey = withdrawnReasonKey

  constructor(private pendingActionService: AssistantPendingActionService) {
    // The card is a single long-lived instance driven by a live listener: the doc can be overwritten in
    // place with a *new* pending action (or resolved to undefined) in the same conversation. Reset the
    // submit flags whenever the action identity changes, so a stale submit-failed error or stuck loading
    // state from a previous action never bleeds onto a fresh one.
    this.sub.add(
      this.pendingAction$.subscribe(action => {
        if (action?.id !== this.submitStateActionId) {
          this.submitStateActionId = action?.id
          this.submitting = false
          this.submitFailed = false
        }
      })
    )
  }

  confirm(action: IAssistantPendingAction): void {
    if (this.submitting) return
    this.submitFailed = false
    this.submitting = true
    this.sub.add(
      this.pendingActionService.confirm(action).subscribe({
        // A non-409 failure keeps the frozen args and re-enables Confirm (distinct from withdrawal).
        error: () => {
          this.submitFailed = true
          this.submitting = false
        },
        // Success (incl. a swallowed 409) clears submitting; the card resolves when the listener
        // advances the conversation off awaiting_confirmation / withdraws the action.
        complete: () => (this.submitting = false),
      })
    )
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe()
  }
}
